import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PublisherPublications from './PublisherPublications';
import PublisherSubscribers from './PublisherSubscribers';
import PublisherTeamPanel from './PublisherTeamPanel';
import PublisherAnalyticsDashboard from './PublisherAnalyticsDashboard';
import PublisherInviteClicks from './PublisherInviteClicks';
import PublisherBalanceEmail from './PublisherBalanceEmail';
import PublisherPointsReminderEmail from './PublisherPointsReminderEmail';
import PublisherCommunications from './PublisherCommunications';
import PublisherDeliverability from './PublisherDeliverability';

const PUBLISHER_JWT_KEY = 'publisher_jwt';

function getJwtClaims() {
  try {
    const token = localStorage.getItem(PUBLISHER_JWT_KEY);
    if (!token) return {};
    return JSON.parse(atob(token.split('.')[1]));
  } catch { return {}; }
}

// ─── Layout ─────────────────────────────────────────────────────────────────

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: linear-gradient(160deg, #08080f 0%, #120822 100%);
  color: white;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const TopBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  height: 58px;
  border-bottom: 1px solid rgba(255,255,255,0.07);
  background: rgba(0,0,0,0.4);
  backdrop-filter: blur(12px);
  position: sticky;
  top: 0;
  z-index: 200;
  flex-shrink: 0;
`;

const TopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const Logo = styled.span`
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: #ff4757;
  text-transform: uppercase;
`;

const ModeBadge = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  background: rgba(255,71,87,0.12);
  border: 1px solid rgba(255,71,87,0.3);
  color: #ff7c89;
  border-radius: 20px;
  padding: 3px 10px;
  letter-spacing: 0.04em;
`;

const PublisherName = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255,255,255,0.85);
  letter-spacing: 0.01em;
`;

const EmailBadge = styled.span`
  font-size: 0.75rem;
  color: rgba(255,255,255,0.35);
`;

const LogoutBtn = styled.button`
  background: none;
  border: 1px solid rgba(255,255,255,0.1);
  color: rgba(255,255,255,0.45);
  border-radius: 20px;
  font-size: 0.72rem;
  padding: 3px 10px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: rgba(255,255,255,0.3); color: white; }
`;

const Body = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const Sidebar = styled.nav`
  width: 220px;
  flex-shrink: 0;
  border-right: 1px solid rgba(255,255,255,0.06);
  background: rgba(0,0,0,0.2);
  display: flex;
  flex-direction: column;
  padding: 24px 12px 20px;
  gap: 4px;
  overflow-y: auto;

  @media (max-width: 640px) {
    display: none;
  }
`;

const MobileNav = styled.div`
  display: none;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  padding: 8px 12px;
  gap: 4px;
  overflow-x: auto;
  background: rgba(0,0,0,0.2);

  @media (max-width: 640px) {
    display: flex;
  }
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 10px;
  background: ${p => p.$active ? 'rgba(255,71,87,0.12)' : 'none'};
  color: ${p => p.$active ? '#ff4757' : 'rgba(255,255,255,0.55)'};
  font-size: 0.88rem;
  font-weight: ${p => p.$active ? '600' : '400'};
  cursor: pointer;
  text-align: left;
  white-space: nowrap;
  transition: background 0.15s, color 0.15s;
  &:hover { background: rgba(255,255,255,0.06); color: white; }
`;

const NavIcon = styled.span`font-size: 1rem; flex-shrink: 0;`;
const NavSpacer = styled.div`flex: 1;`;
const NavDivider = styled.div`
  height: 1px;
  background: rgba(255,255,255,0.06);
  margin: 8px 4px;
`;

const Main = styled.main`
  flex: 1;
  padding: 32px 28px;
  overflow-y: auto;

  @media (max-width: 640px) {
    padding: 20px 16px;
  }
`;

// ─── Dashboard ────────────────────────────────────────────────────────────────

const PublisherDashboard = ({ initialView = 'publications' }) => {
  const navigate = useNavigate();
  const claims = getJwtClaims();
  const isOwner = claims.role === 'owner';
  const [activeView, setActiveView] = useState(initialView);

  const handleLogout = () => {
    localStorage.removeItem(PUBLISHER_JWT_KEY);
    navigate('/publisher/login', { replace: true });
  };

  const navItems = [
    { key: 'publications', icon: '📰', label: 'Publications' },
    { key: 'subscribers',  icon: '👥', label: 'Subscribers' },
    { key: 'analytics',   icon: '📊', label: 'Analytics' },
    { key: 'invite-clicks', icon: '🔗', label: 'Invite Clicks' },
    { key: 'deliverability', icon: '📈', label: 'Deliverability' },
    ...(isOwner ? [{ key: 'balance-email', icon: '✉️', label: 'Balance Email' }] : []),
    ...(isOwner ? [{ key: 'points-reminder', icon: '⏳', label: 'Points Reminder' }] : []),
    ...(isOwner ? [{ key: 'communications', icon: '📣', label: 'Communications' }] : []),
    ...(isOwner ? [{ key: 'team', icon: '🤝', label: 'Team' }] : []),
  ];

  return (
    <Shell>
      <TopBar>
        <TopLeft>
          <Logo>VuSub</Logo>
          <ModeBadge>Publisher Portal</ModeBadge>
          {claims.publisher_name && <PublisherName>{claims.publisher_name}</PublisherName>}
          {claims.sub && <EmailBadge>{claims.sub}</EmailBadge>}
        </TopLeft>
        <LogoutBtn onClick={handleLogout}>Log out</LogoutBtn>
      </TopBar>

      <MobileNav>
        {navItems.map(n => (
          <NavItem key={n.key} $active={activeView === n.key} onClick={() => setActiveView(n.key)}
            style={{ width: 'auto', flex: '0 0 auto' }}>
            <NavIcon>{n.icon}</NavIcon>{n.label}
          </NavItem>
        ))}
      </MobileNav>

      <Body>
        <Sidebar>
          {navItems.map(n => (
            <NavItem key={n.key} $active={activeView === n.key} onClick={() => setActiveView(n.key)}>
              <NavIcon>{n.icon}</NavIcon>{n.label}
            </NavItem>
          ))}
          <NavSpacer />
          <NavDivider />
          <NavItem $active={false} onClick={handleLogout} style={{ color: 'rgba(255,255,255,0.35)' }}>
            <NavIcon>🚪</NavIcon>Log out
          </NavItem>
        </Sidebar>

        <Main>
          {activeView === 'publications' && <PublisherPublications isOwner={isOwner} />}
          {activeView === 'subscribers' && <PublisherSubscribers isOwner={isOwner} />}
          {activeView === 'analytics' && <PublisherAnalyticsDashboard />}
          {activeView === 'invite-clicks' && <PublisherInviteClicks />}
          {activeView === 'balance-email' && isOwner && <PublisherBalanceEmail />}
          {activeView === 'points-reminder' && isOwner && <PublisherPointsReminderEmail />}
          {activeView === 'communications' && isOwner && <PublisherCommunications />}
          {activeView === 'deliverability' && <PublisherDeliverability />}
          {activeView === 'team' && isOwner && <PublisherTeamPanel />}
        </Main>
      </Body>
    </Shell>
  );
};

export default PublisherDashboard;
